'use client';
import * as RadixDialog from '@radix-ui/react-dialog';
import { ReactNode } from 'react';
import { twMerge } from '../../utils/tw-merge';
import SvgClose from '../icons/Close';
import SvgIcon from '../svg-icon/svg-icon';

interface ContainerProps {
    open: boolean;
    onClose: () => void;
    container?: Element | null;
    children: ReactNode;
}

export function Container(props: ContainerProps) {
    return (
        <RadixDialog.Root open={props.open} onOpenChange={props.onClose}>
            <RadixDialog.Portal container={props.container}>
                <RadixDialog.Overlay className="bg-mono-0-opacity-60 fixed inset-0 z-50 flex items-center justify-center">
                    <RadixDialog.Content className="shadow-popover bg-background-default-interactive-surface absolute top-0 right-0 bottom-0 left-0 z-50 items-center space-y-4 overflow-y-auto p-3 md:top-6/12 md:bottom-[unset] md:left-6/12 md:max-h-[90vh] md:w-[600px] md:-translate-6/12 md:px-[75px] md:py-[30px]">
                        {props.children}
                    </RadixDialog.Content>
                </RadixDialog.Overlay>
            </RadixDialog.Portal>
        </RadixDialog.Root>
    );
}

interface TitleProps {
    children?: ReactNode;
    className?: string;
}

export function Title(props: TitleProps) {
    const titleClasses = twMerge('font-style-display-2 not-last:mb-5 first:pr-12', props.className);

    return (
        <RadixDialog.Title asChild className={titleClasses}>
            {props.children}
        </RadixDialog.Title>
    );
}

interface DescriptionProps {
    children: ReactNode;
    className?: string;
}

export function Description(props: DescriptionProps) {
    const descriptionClasses = twMerge('first:*:first:pr-12', props.className);

    return (
        <RadixDialog.Description className={descriptionClasses} asChild>
            {props.children}
        </RadixDialog.Description>
    );
}

interface CloseProps {
    children?: ReactNode;
    className?: string;
}

export function Close(props: CloseProps) {
    const closeClasses = twMerge(
        'absolute top-4 right-4 cursor-pointer p-2 md:top-[30px] md:right-[75px]',
        props.className,
    );

    return (
        <RadixDialog.Close className={closeClasses}>
            {props.children ?? <SvgIcon icon={SvgClose} size={16} className="text-interface-text" />}
        </RadixDialog.Close>
    );
}

interface ButtonsContainerProps {
    children: ReactNode;
    className?: string;
}

export function ButtonsContainer(props: ButtonsContainerProps) {
    const buttonsContainerClasses = twMerge(
        'flex justify-center items-center flex-col gap-6 mb-0 md:flex-row mt-[30px]',
        props.className,
    );

    return <div className={buttonsContainerClasses}>{props.children}</div>;
}
